/**
 * VideoCompareView Component
 *
 * Side-by-side video comparison for form review.
 * Supports synchronized playback and overlay mode.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import {
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Maximize2,
  Columns,
  Layers,
  Link,
  Unlink,
  SkipBack,
  SkipForward,
} from "lucide-react";

type CompareMode = "side-by-side" | "overlay";

interface VideoCompareViewProps {
  leftVideoUrl: string;
  rightVideoUrl: string;
  leftLabel?: string;
  rightLabel?: string;
  className?: string;
  onClose?: () => void;
}

export function VideoCompareView({
  leftVideoUrl,
  rightVideoUrl,
  leftLabel = "Reference",
  rightLabel = "Your Form",
  className,
  onClose,
}: VideoCompareViewProps) {
  const leftVideoRef = useRef<HTMLVideoElement>(null);
  const rightVideoRef = useRef<HTMLVideoElement>(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSynced, setIsSynced] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [leftMuted, setLeftMuted] = useState(false);
  const [rightMuted, setRightMuted] = useState(true);

  // View mode
  const [mode, setMode] = useState<CompareMode>("side-by-side");
  const [overlayOpacity, setOverlayOpacity] = useState(0.5);

  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle play/pause
  const togglePlay = useCallback(() => {
    const leftVideo = leftVideoRef.current;
    const rightVideo = rightVideoRef.current;
    if (!leftVideo || !rightVideo) return;

    if (isPlaying) {
      leftVideo.pause();
      rightVideo.pause();
    } else {
      leftVideo.play();
      rightVideo.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  // Handle seek
  const handleSeek = useCallback(
    (value: number[]) => {
      const newTime = value[0];
      const leftVideo = leftVideoRef.current;
      const rightVideo = rightVideoRef.current;
      if (!leftVideo || !rightVideo) return;

      leftVideo.currentTime = newTime;
      if (isSynced) {
        rightVideo.currentTime = newTime;
      }
      setCurrentTime(newTime);
    },
    [isSynced]
  );

  // Handle restart
  const handleRestart = () => {
    const leftVideo = leftVideoRef.current;
    const rightVideo = rightVideoRef.current;
    if (!leftVideo || !rightVideo) return;

    leftVideo.currentTime = 0;
    rightVideo.currentTime = 0;
    setCurrentTime(0);
  };

  // Skip forward/back
  const handleSkip = (seconds: number) => {
    const leftVideo = leftVideoRef.current;
    const rightVideo = rightVideoRef.current;
    if (!leftVideo || !rightVideo) return;

    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    leftVideo.currentTime = newTime;
    if (isSynced) {
      rightVideo.currentTime = newTime;
    }
    setCurrentTime(newTime);
  };

  // Sync right video to left when sync is enabled
  const syncVideos = useCallback(() => {
    const leftVideo = leftVideoRef.current;
    const rightVideo = rightVideoRef.current;
    if (!leftVideo || !rightVideo || !isSynced) return;

    rightVideo.currentTime = leftVideo.currentTime;
  }, [isSynced]);

  // Update duration when video loads
  useEffect(() => {
    const leftVideo = leftVideoRef.current;
    if (!leftVideo) return;

    const handleLoadedMetadata = () => {
      setDuration(leftVideo.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(leftVideo.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    leftVideo.addEventListener("loadedmetadata", handleLoadedMetadata);
    leftVideo.addEventListener("timeupdate", handleTimeUpdate);
    leftVideo.addEventListener("ended", handleEnded);

    return () => {
      leftVideo.removeEventListener("loadedmetadata", handleLoadedMetadata);
      leftVideo.removeEventListener("timeupdate", handleTimeUpdate);
      leftVideo.removeEventListener("ended", handleEnded);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;

      switch (e.key) {
        case " ":
          e.preventDefault();
          togglePlay();
          break;
        case "ArrowLeft":
          handleSkip(-5);
          break;
        case "ArrowRight":
          handleSkip(5);
          break;
        case "s":
          setIsSynced((prev) => !prev);
          break;
        case "m":
          setMode((prev) => (prev === "side-by-side" ? "overlay" : "side-by-side"));
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [togglePlay]);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Mode Toggle */}
      <div className="flex items-center justify-between">
        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(v) => v && setMode(v as CompareMode)}
        >
          <ToggleGroupItem value="side-by-side" className="gap-2">
            <Columns className="h-4 w-4" />
            Side by Side
          </ToggleGroupItem>
          <ToggleGroupItem value="overlay" className="gap-2">
            <Layers className="h-4 w-4" />
            Overlay
          </ToggleGroupItem>
        </ToggleGroup>

        <Button
          variant={isSynced ? "default" : "outline"}
          size="sm"
          onClick={() => {
            setIsSynced(!isSynced);
            if (!isSynced) syncVideos();
          }}
          className="gap-2"
        >
          {isSynced ? (
            <>
              <Link className="h-4 w-4" />
              Synced
            </>
          ) : (
            <>
              <Unlink className="h-4 w-4" />
              Independent
            </>
          )}
        </Button>
      </div>

      {/* Video Container */}
      {mode === "side-by-side" ? (
        <div className="grid grid-cols-2 gap-4">
          {/* Left Video */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Badge variant="outline">{leftLabel}</Badge>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setLeftMuted(!leftMuted)}
              >
                {leftMuted ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </Button>
            </div>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <video
                ref={leftVideoRef}
                src={leftVideoUrl}
                className="w-full h-full object-contain"
                muted={leftMuted}
                playsInline
              />
            </div>
          </div>

          {/* Right Video */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Badge variant="outline">{rightLabel}</Badge>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setRightMuted(!rightMuted)}
              >
                {rightMuted ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </Button>
            </div>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <video
                ref={rightVideoRef}
                src={rightVideoUrl}
                className="w-full h-full object-contain"
                muted={rightMuted}
                playsInline
              />
            </div>
          </div>
        </div>
      ) : (
        /* Overlay Mode */
        <div className="space-y-4">
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            {/* Base video */}
            <video
              ref={leftVideoRef}
              src={leftVideoUrl}
              className="absolute inset-0 w-full h-full object-contain"
              muted={leftMuted}
              playsInline
            />

            {/* Overlay video */}
            <video
              ref={rightVideoRef}
              src={rightVideoUrl}
              className="absolute inset-0 w-full h-full object-contain"
              style={{ opacity: overlayOpacity }}
              muted={rightMuted}
              playsInline
            />

            {/* Labels */}
            <div className="absolute top-2 left-2 flex gap-2">
              <Badge variant="secondary" className="bg-black/60 text-white">
                {leftLabel}
              </Badge>
            </div>
            <div className="absolute top-2 right-2 flex gap-2">
              <Badge variant="secondary" className="bg-black/60 text-white">
                {rightLabel} ({Math.round(overlayOpacity * 100)}%)
              </Badge>
            </div>
          </div>

          {/* Opacity Slider */}
          <div className="flex items-center gap-4">
            <Label className="text-sm shrink-0">Overlay Opacity</Label>
            <Slider
              value={[overlayOpacity]}
              onValueChange={([value]) => setOverlayOpacity(value)}
              min={0}
              max={1}
              step={0.05}
              className="flex-1"
            />
            <span className="text-sm font-mono w-12">
              {Math.round(overlayOpacity * 100)}%
            </span>
          </div>
        </div>
      )}

      {/* Playback Controls */}
      <div className="space-y-3 p-4 bg-muted rounded-lg">
        {/* Progress Bar */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono w-12">{formatTime(currentTime)}</span>
          <Slider
            value={[currentTime]}
            onValueChange={handleSeek}
            max={duration || 100}
            step={0.1}
            className="flex-1"
          />
          <span className="text-xs font-mono w-12">{formatTime(duration)}</span>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-center gap-2">
          <Button variant="ghost" size="icon" onClick={handleRestart}>
            <RotateCcw className="h-4 w-4" />
          </Button>

          <Button variant="ghost" size="icon" onClick={() => handleSkip(-10)}>
            <SkipBack className="h-4 w-4" />
          </Button>

          <Button
            variant="default"
            size="icon"
            className="h-10 w-10"
            onClick={togglePlay}
          >
            {isPlaying ? (
              <Pause className="h-5 w-5" />
            ) : (
              <Play className="h-5 w-5 ml-0.5" />
            )}
          </Button>

          <Button variant="ghost" size="icon" onClick={() => handleSkip(10)}>
            <SkipForward className="h-4 w-4" />
          </Button>
        </div>

        {/* Keyboard shortcuts hint */}
        <p className="text-xs text-muted-foreground text-center">
          Space: play/pause | ← →: skip | S: toggle sync | M: toggle mode
        </p>
      </div>
    </div>
  );
}

export default VideoCompareView;
